import type { Clip, Segment, SfxMoment } from "./clip.js";
import { sourcePointToTimeline } from "./direction.js";
import { timelineDuration } from "./timing.js";

/** A sound the clip plays during the speech: at a cut, or at a moment the owner placed. `atMs` is speech-timeline ms (0 = the first piece's start). */
export interface SfxCue { atMs: number; soundId: string; levelDb: number; kind: "cut" | "moment"; momentId?: string }

/** Every sound cue in speech order: the cut sound at each piece after the first, and each placed moment that still falls inside the pieces. */
export function sfxCues(clip: Pick<Clip, "segments" | "sfx">): SfxCue[] {
  const out: SfxCue[] = [];
  if (clip.sfx.cuts && clip.sfx.cutsId) {
    let acc = 0;
    clip.sfx.cutsId && clip.segments.forEach((s, i) => { if (i > 0) out.push({ atMs: acc, soundId: clip.sfx.cutsId!, levelDb: clip.sfx.levels.cuts, kind: "cut" }); acc += s.outMs - s.inMs; });
  }
  for (const m of clip.sfx.moments) {
    const at = sourcePointToTimeline(clip.segments, m.atMs);
    if (at != null) out.push({ atMs: at, soundId: m.soundId, levelDb: m.levelDb, kind: "moment", momentId: m.id });
  }
  return out.sort((a, b) => a.atMs - b.atMs);
}

/** Where a placed sound sits on the speech timeline, for lists and markers (null when it fell in a cut). */
export const sfxMomentTimelineMs = (segments: Segment[], m: SfxMoment): number | null => sourcePointToTimeline(segments, m.atMs);

/** The voice's fade factor (0..1) at a speech-timeline moment: linear in gain, over the first `inMs` and the last `outMs` of the speech. */
export function voiceFadeGain(speechMs: number, segments: Segment[], fade: { inMs: number; outMs: number }): number {
  const total = timelineDuration(segments);
  let g = 1;
  if (fade.inMs > 0 && speechMs < fade.inMs) g = Math.min(g, Math.max(0, speechMs / fade.inMs));
  if (fade.outMs > 0 && speechMs > total - fade.outMs) g = Math.min(g, Math.max(0, (total - speechMs) / fade.outMs));
  return Math.max(0, Math.min(1, g));
}

export const sfxMomentId = (): string => `sm-${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36).padStart(2, "0")}`;
